import { useMemo } from 'react'
import { useAppAuth } from '@/contexts/app-auth.context'
import { graphsAdminApiClient, graphsApiClient } from '@/lib/api'
import type { SaveGraphRequest } from '@/lib/api/graphs.client'

/**
 * Company-correct graphs API: super admins address the company selected in the
 * switcher (client-side selection → admin endpoints with ?companyId=), regular
 * users ride their session company. Memoized on the selected company so pages
 * re-fetch when the switcher changes.
 */
export function useGraphsClient() {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id

  return useMemo(() => {
    if (isSuperAdmin && companyId) {
      return {
        list: () => graphsAdminApiClient.list(companyId),
        get: (id: string) => graphsAdminApiClient.get(companyId, id),
        create: (dto: SaveGraphRequest) =>
          graphsAdminApiClient.create(companyId, dto),
        save: (id: string, dto: SaveGraphRequest) =>
          graphsAdminApiClient.save(companyId, id, dto),
        remove: (id: string) => graphsAdminApiClient.remove(companyId, id),
      }
    }
    return {
      list: () => graphsApiClient.list(),
      get: (id: string) => graphsApiClient.get(id),
      create: (dto: SaveGraphRequest) => graphsApiClient.create(dto),
      save: (id: string, dto: SaveGraphRequest) =>
        graphsApiClient.save(id, dto),
      remove: (id: string) => graphsApiClient.remove(id),
    }
  }, [isSuperAdmin, companyId])
}
